import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Auth0Client, User } from '@auth0/auth0-spa-js';
import { AuthContext } from './authContext';
import { hasAuthParams } from './hasAuthParams';
import { clearLoginSession, isLoginSessionValid, saveLoginSession } from './loginSession';
import type { IdTokenClaims } from '#/lib/claims';

export function AuthProvider({
  client,
  onRedirectCallback,
  children,
}: {
  client: Auth0Client;
  onRedirectCallback: () => void;
  children: ReactNode;
}) {
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [claims, setClaims] = useState<IdTokenClaims | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    (async (): Promise<void> => {
      try {
        let user: User | undefined;
        if (hasAuthParams()) {
          await client.handleRedirectCallback();
          user = await client.getUser();
          saveLoginSession();
          onRedirectCallback();
        } else if (isLoginSessionValid()) {
          await client.checkSession();
          user = await client.getUser();
        } else {
          clearLoginSession();
        }
        if (user) {
          const idTokenClaims = await client.getIdTokenClaims();
          setClaims((idTokenClaims as IdTokenClaims | undefined) ?? null);
        }
        setIsAuthenticated(!!user);
      } catch (e: unknown) {
        // Error 以外が throw されても握りつぶさず error をセットする
        // （未設定だと未認証扱いで結果画面から黙ってトップへ遷移してしまうため）。
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [client, onRedirectCallback]);

  const login = useCallback(async () => {
    await client.loginWithRedirect();
  }, [client]);

  const logout = useCallback(async () => {
    clearLoginSession();
    await client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/`,
      },
    });
  }, [client]);

  return (
    <AuthContext.Provider value={{ error, isLoading, isAuthenticated, claims, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
